import { selectedCustomer } from '@app/ngrx-customer/store';
import { createSelector } from '@ngrx/store';
import { ProductFilter } from '@shared/helpers/product-filter.helpers';
import { productWithAlternativesSelector } from '@shared/selectors/product.selectors';
import { selectMerchandisingResponseByIds } from '@usf/ngrx-product';
import {
  DeliveriesViewModel,
  Experience,
} from '../models/deliveries-view-model';
import { MerchandisingProductViewModel } from '@shared/models/merchandising-product-view.model';
import { selectAllFeaturesEnabled } from '@panamax/app-state';
import { FEATURES } from '@shared/constants/splitio-features';
import { isProprietaryProduct } from '@product-detail/utils/product-summary.utils';

export const selectDeliveriesViewModel = (experiences: string[]) =>
  createSelector(
    selectedCustomer,
    selectMerchandisingResponseByIds(experiences),
    productWithAlternativesSelector,
    selectAllFeaturesEnabled([FEATURES.split_dynamic_proprietary_ui.name]),
    (
      selectedCustomer,
      merchandisingStates,
      products,
      isProprietaryUIEnabled,
    ): DeliveriesViewModel => {
      const deliveriesViewModel: DeliveriesViewModel = {
        experiences: [],
      };

      merchandisingStates?.forEach(merchandisingState => {
        const experienceProducts: MerchandisingProductViewModel[] = [];

        if (
          merchandisingState?.products?.every(
            product => products.get(product?.productNumber) !== undefined,
          )
        ) {
          merchandisingState?.products?.forEach(merchandisingProduct => {
            const product = products?.get(merchandisingProduct?.productNumber);

            if (
              !!product &&
              ProductFilter.applyFilter(product, selectedCustomer) &&
              !isProprietaryProduct(product, isProprietaryUIEnabled)
            ) {
              if (!!product?.summary?.properties) {
                product.summary.properties = new Set([
                  ...product?.summary?.properties,
                  ...product?.summary?.additionalProperties,
                ]);
              }
              experienceProducts.push({
                ...product,
                selectedCustomer,
                recToken: merchandisingProduct?.recToken,
              } as MerchandisingProductViewModel);
            }
          });
        }

        const experience: Experience = {
          name: merchandisingState?.experience,
          variant: merchandisingState?.variant,
          products: experienceProducts,
        };
        deliveriesViewModel.experiences.push(experience);
      });

      return deliveriesViewModel;
    },
  );

export const selectDeliveriesProductNumbers = (experiences: string[]) =>
  createSelector(
    selectMerchandisingResponseByIds(experiences),
    (merchandisingStates): number[] => {
      const productNumberSet = new Set<number>();

      merchandisingStates?.forEach(merchandisingState =>
        merchandisingState?.products?.forEach(product =>
          productNumberSet.add(product?.productNumber),
        ),
      );

      return Array.from(productNumberSet);
    },
  );
